/**
 * Enhanced Project Summary Page - Jira-like with exact layout
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { updateTask } from '@/store/taskSlice';
import { projectAPI } from '@/api/projectAPI';
import { taskAPI } from '@/api/taskAPI';
import { commentAPI } from '@/api/commentAPI';
import { projectMemberAPI } from '@/api/projectMemberAPI';
import { subtaskAPI } from '@/api/subtaskAPI';
import { mastersAPI, WorkflowStatus, TaskType, Priority } from '@/api/mastersApi';
import { TaskModal } from '@/components/TaskModal/TaskModal';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { CheckCircle, Edit, CheckSquare, Calendar, ExternalLink } from 'lucide-react';
import { Loader } from '@/components/Loader';
import { deleteComment, formatCommentsForActivity, updateComment } from '@/utils/commentUtils';
import { ensureHtml, toPlainText } from '@/utils/richText';
import { CommentActivity, Comment as ProjectComment } from '@/types/comments';
import SubtaskModal from '@/components/SubtaskModal/SubtaskModal';
import type { Subtask } from '@/types';
import { updateSubtask } from '@/store/taskSlice';
import { API_BASE_URL } from '@/utils/constants';
import { TaskTypeIcon } from '@/components/TaskTypeIcon';
import { getTasksDueWithin7Days } from '@/utils/daysCalculator';

interface TaskData {
  id: string;
  title: string;
  status: string;
  priority: string;
  task_type: string;
  assigned_to?: string;
  created_at?: string;
  updated_at?: string;
  project_id?: string;
}

interface SubtaskData {
  id: string;
  title: string;
  description?: string;
  task_id: string;
  status: 'todo' | 'in_progress' | 'done' | string;
  order: number;
  is_completed: boolean;
  project_id?: string;
  assigned_to?: string;
  created_at: string;
  updated_at: string;
}


const resolveTaskTypeIconUrl = (raw?: string | null) => {
  if (!raw) return null;
  const builtInIcons = ['task', 'bug', 'story', 'epic', 'subtask'];
  if (builtInIcons.includes(raw.toLowerCase())) return null;
  try {
    if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) {
      return raw;
    }
    const api = new URL(API_BASE_URL);
    const origin = `${api.protocol}//${api.host}`;
    const path = raw.startsWith('/') ? raw : `/${raw}`;
    return origin + path;
  } catch {
    return null;
  }
};

interface ProjectSummaryData {
  id: string;
  name: string;
  key: string;
  total_tasks: number;
  completed_tasks: number;
  in_progress_tasks: number;
  backlog_tasks: number;
  total_story_points: number;
  completion_percentage: number;
  active_sprints: number;
  team_members: number;
}

export const Summary: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentProject } = useAppSelector((s) => s.projects);

  // project members state
  const [projectMembers, setProjectMembers] = useState<any[]>([]);

  // Load project members when project changes
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!projectId) {
        if (mounted) setProjectMembers([]);
        return;
      }
      try {
        const data = await projectMemberAPI.getProjectMembers(projectId);
        if (mounted) setProjectMembers(data || []);
      } catch (error) {
        console.error('Failed to fetch project members:', error);
        if (mounted) setProjectMembers([]);
      }
    })();
    return () => { mounted = false; };
  }, [projectId]);

  const [summary, setSummary] = useState<ProjectSummaryData | null>(null);
  const [tasks, setTasks] = useState<TaskData[]>([]);
  const [subtasks, setSubtasks] = useState<SubtaskData[]>([]);
  const [statusGroups, setStatusGroups] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);

  // Comments and activity used by TaskModal
  const [comments, setComments] = useState<ProjectComment[]>([]);
  const [activity, setActivity] = useState<CommentActivity[]>([]);

  // Task modal
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  // Related work / comments / activity for TaskModal
  // related work removed in modal props

  // Subtask Modal States
  const [activeSubtask, setActiveSubtask] = useState<Subtask | null>(null);
  const [isSubtaskModalOpen, setIsSubtaskModalOpen] = useState(false);
  const [parentTaskForSubtask, setParentTaskForSubtask] = useState<any>(null);


  // Boot loading aggregation similar to Kanban
  const [bootLoading, setBootLoading] = useState(true);
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [subtasksLoaded, setSubtasksLoaded] = useState(false);
  const [mastersLoaded, setMastersLoaded] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadData();
    }
  }, [projectId]);

  const loadData = async () => {
    try {
      setError(null);

      // reset boot flags for new load
      setSummaryLoaded(false);
      setTasksLoaded(false);
      setSubtasksLoaded(false);
      setMastersLoaded(false);
      if(!projectId){
        throw new Error("No project Id is available")
    }
      // Load all data in parallel
      const [summaryRes, tasksRes, statusesRes, typesRes, prioritiesRes, subtasksRes] = await Promise.allSettled([
        projectAPI.getProjectSummary(projectId!),
        taskAPI.getTasks({ project_id: projectId, limit: 1000 }),
        mastersAPI.getWorkflowStatusesList(projectId as string),
        mastersAPI.getTaskTypes(),
        mastersAPI.getPriorities(),
        taskAPI.getAllSubtasks({ project_id: projectId, limit: 1000 }),
      ]);

      // Summary
      if (summaryRes.status === 'fulfilled') {
        setSummary(summaryRes.value as any);
      } else {
        setError('Failed to load project summary');
      }
      setSummaryLoaded(true);

      // Tasks
      let tasksData: any[] = [];
      if (tasksRes.status === 'fulfilled') {
        tasksData = tasksRes.value as any[];
        setTasks(tasksData as any);
      } else {
        setError(prev => prev || 'Failed to load tasks');
      }
      setTasksLoaded(true);

      let subtasksData: any[] = [];
      if (subtasksRes.status === 'fulfilled') {
        subtasksData = subtasksRes.value as any[];
        setSubtasks(subtasksData as any);
      } else {
        setError(prev => prev || 'Failed to load subtasks');
      }
      setSubtasksLoaded(true);

      // Masters
      const statuses = statusesRes.status === 'fulfilled' ? statusesRes.value.statuses : [];
      const types = typesRes.status === 'fulfilled' ? typesRes.value : [];
      const prios = prioritiesRes.status === 'fulfilled' ? prioritiesRes.value : [];
      setWorkflowStatuses(statuses as any);
      setTaskTypes(types as any);
      setPriorities(prios as any);
      setMastersLoaded(true);

      // Group tasks by workflow status (using mapped names)
      const statusNameById = new Map<string, string>();
      (statuses as any[]).forEach((s: any) => {
        if (s?.id && s?.name) statusNameById.set(s.id, String(s.name).toLowerCase());
      });
      const grouped: Record<string, number> = {};
      (tasksData as any[]).forEach((task) => {
        const workflowStatusId = (task as any).workflow_status_id;
        if (workflowStatusId && statusNameById.has(workflowStatusId)) {
          const statusName = statusNameById.get(workflowStatusId)!;
          grouped[statusName] = (grouped[statusName] || 0) + 1;
        }
      });
      setStatusGroups(grouped);
    } catch (err) {
      console.error('Failed to load summary:', err);
      setError('Something went wrong while loading summary');
      setSummaryLoaded(true);
      setTasksLoaded(true);
      setSubtasksLoaded(true);
      setMastersLoaded(true);
    } finally {
      setLoading(false);
    }
  }

  // Aggregate boot loading
  useEffect(() => {
    if (summaryLoaded && tasksLoaded && subtasksLoaded && mastersLoaded) {
      setBootLoading(false);
    } else {
      setBootLoading(true);
    }
  }, [summaryLoaded, tasksLoaded, subtasksLoaded, mastersLoaded]);

  const handleStatusClick = (status: string) => {
    navigate(`/projects/${projectId}/list?status=${status}`);
  };

  const handleDueSoon = ()=>{
    navigate(`/projects/${projectId}/list?dueSoon=true`)
  }

  const handleUpdatedClick = () => {
    // Navigate to show all tasks updated in last 7 days
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const isoDate = sevenDaysAgo.toISOString();
    navigate(`/projects/${projectId}/list?updated_after=${isoDate}`);
  };

  const handleViewAllClick = () => {
    navigate(`/projects/${projectId}/list`);
  };

  const handlePriorityClick = (priority: string) => {
    navigate(`/projects/${projectId}/list?priority=${priority}`);
  };

  const handleTypeClick = (type: string) => {
    navigate(`/projects/${projectId}/list?task_type=${type}`);
  };

  // load comments for a task and format activity
  const loadComments = async (taskId: string) => {
    try {
      const c: ProjectComment[] = await commentAPI.getComments(taskId);
      setComments(c);
      setActivity(formatCommentsForActivity(c));
    } catch (err) {
      console.error('loadComments failed', err);
      setComments([]);
      setActivity([]);
    }
  };

  // related work helper removed

  // Following BacklogView pattern - newStatus is already the workflow_status_id
  const handleStatusUpdate = async (taskId: string, workflowStatusId: string) => {
    try {
      await dispatch(updateTask({
        id: taskId,
        data: { workflow_status_id: workflowStatusId || undefined }
      })).unwrap();

      // Refresh the selected task to reflect the status change
      if (selectedTask?.id === taskId) {
        try {
          const fresh = await taskAPI.getTask(taskId);
          setSelectedTask(fresh);
        } catch { /* ignore */ }
      }

      await loadData();
    } catch (e) {
      console.error('status update failed', e);
    }
  };

  // Helper function to refresh project members
  const refreshProjectMembers = async (projectId?: string) => {
    const targetProjectId = projectId || currentProject?.id;
    if (!targetProjectId) {
      return;
    }
    try {
      const data = await projectMemberAPI.getProjectMembers(targetProjectId);
      setProjectMembers(data || []);
    } catch (error) {
      console.error('Failed to fetch project members:', error);
    }
  };

  // replace handleTaskClick to fetch full task from API (ensures modal has description, subtasks, assignee, etc)
  const isSubtaskEntity = (item: TaskData | SubtaskData): item is SubtaskData => {
    return typeof (item as SubtaskData)?.task_id === 'string' && Boolean((item as SubtaskData).task_id);
  };

  const handleTaskClick = async (task: TaskData | SubtaskData) => {
    const isSubtask = isSubtaskEntity(task);

    try {
      if (isSubtask) {
        const parentId = task.task_id;
        const [parentTask, parentSubtasks] = await Promise.all([
          taskAPI.getTask(parentId),
          taskAPI.getSubtasks(parentId)
        ]);

        const resolvedProjectId = parentTask?.project_id || task.project_id || currentProject?.id;
        if (resolvedProjectId) {
          await refreshProjectMembers(resolvedProjectId);
        }

        const matchedSubtask = parentSubtasks.find((sub) => String(sub.id) === String(task.id));
        setParentTaskForSubtask(parentTask as any);
        setActiveSubtask((matchedSubtask as Subtask) || (task as any));
        setIsTaskModalOpen(false);
        setIsSubtaskModalOpen(true);
        return;
      }

      // Always refresh project members to ensure we have the latest list
      if (task.project_id || currentProject?.id) {
        await refreshProjectMembers(task.project_id || currentProject?.id);
      }

      // try to fetch full task directly from API for reliable data
      const full = await taskAPI.getTask(task.id);
      setSelectedTask(full || task);

      // load comments & activity
      await loadComments(task.id);
      // related work fetch removed

      setIsTaskModalOpen(true);
    } catch (err) {
      console.error('failed to load task details for modal, falling back to basic task', err);

      if (isSubtask) {
        setParentTaskForSubtask((prev: any) => prev || (task as any));
        setActiveSubtask(task as any);
        setIsTaskModalOpen(false);
        setIsSubtaskModalOpen(true);
        return;
      }

      // fallback behavior: still open modal with basic task and loaded comments
      setSelectedTask(task);
      await loadComments(task.id);
      // related work fetch removed
      setIsTaskModalOpen(true);
    }
  };

  // Create lookup maps for master data
  const statusMap = new Map(workflowStatuses.map(s => [s.id, s]));
  const typeMap = new Map(taskTypes.map(t => [t.id, t]));
  const priorityMap = new Map(priorities.map(p => [p.id, p]));

  // Helper function to get status name from workflow_status_id
  const getStatusName = (task: any): string => {
    if (!task.workflow_status_id) return '';
    const status = statusMap.get(task.workflow_status_id);
    return status?.name.toLowerCase() || '';
  };

  // Helper function to check if status is completed
  const isCompleted = (statusName: string): boolean => {
    const status = Array.from(statusMap.values()).find(s =>
      s.name.toLowerCase() === statusName.toLowerCase()
    );
    return status?.is_final || statusName.toLowerCase().includes('done') ||
      statusName.toLowerCase().includes('completed') ||
      statusName.toLowerCase().includes('complete');
  };

  // Calculate metrics for last 7 days
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const completedLast7Days = tasks.filter(t => {
    const updated = new Date(t.updated_at || t.created_at || 0);
    const statusName = getStatusName(t);
    return updated >= sevenDaysAgo && isCompleted(statusName);
  }).length;

  const updatedLast7Days = tasks.filter(t => {
    const updated = new Date(t.updated_at || 0);
    return updated >= sevenDaysAgo;
  }).length;

  const createdLast7Days = tasks.filter(t => {
    const created = new Date(t.created_at || 0);
    return created >= sevenDaysAgo;
  }).length;

  const sevenDaysFromNow = new Date();
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
  const dueSoon = tasks.length ? getTasksDueWithin7Days(tasks).length : 0;

  // Get recent activity (last 5 tasks by updated_at)
  const recentActivity = [...tasks, ...subtasks]
    .sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
      const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
      return dateB - dateA;
    })
    .slice(0, 5);

  // Prepare chart data
  const chartData = Object.entries(statusGroups)
    .sort((a, b) => b[1] - a[1])
    .map(([status, count]) => ({
      name: status.toUpperCase().replace(/_/g, ' '),
      value: count,
      status: status
    }));

  // Prepare priority breakdown data using priority_id
  const priorityGroups: Record<string, number> = {};
  tasks.forEach(task => {
    const priorityId = (task as any).priority_id;
    if (priorityId && priorityMap.has(priorityId)) {
      const priority = priorityMap.get(priorityId)!;
      const priorityKey = priority.key || priority.name.toLowerCase();
      priorityGroups[priorityKey] = (priorityGroups[priorityKey] || 0) + 1;
    }
  });

  // Get all priorities sorted by order and map to chart data
  const sortedPriorities = [...priorities].sort((a, b) => a.order - b.order);
  const priorityData = sortedPriorities.map(priority => ({
    name: priority.name,
    value: priorityGroups[priority.key || priority.name.toLowerCase()] || 0,
    priority: priority.key || priority.name.toLowerCase()
  }));

  // Prepare task type breakdown data using task_type_id
  const typeGroups: Record<string, { count: number; name: string }> = {};
  tasks.forEach(task => {
    const taskTypeId = (task as any).task_type_id;
    if (taskTypeId && typeMap.has(taskTypeId)) {
      const taskType = typeMap.get(taskTypeId)!;
      const typeKey = taskType.name.toLowerCase();
      if (!typeGroups[typeKey]) {
        typeGroups[typeKey] = { count: 0, name: taskType.name };
      }
      typeGroups[typeKey].count++;
    }
  });

  // Get all task types sorted by name, ensuring core types are shown even if 0
  const coreTypes = ['task', 'bug', 'story', 'epic', 'subtask'];
  const allTaskTypes = [...taskTypes];

  // Add core types if they don't exist in the master data
  coreTypes.forEach(coreType => {
    const exists = allTaskTypes.some(tt => tt.name.toLowerCase() === coreType);
    if (!exists) {
      // Create a placeholder entry
      allTaskTypes.push({
        id: coreType,
        name: coreType.charAt(0).toUpperCase() + coreType.slice(1),
        is_active: true
      } as TaskType);
    }
  });

  const totalTasks = tasks.length;
  const typeData = allTaskTypes
    .filter(tt => tt.is_active !== false)
    .map((taskType) => {
      const typeKey = taskType.name.toLowerCase();
      const groupData = typeGroups[typeKey] || { count: 0, name: taskType.name };
      return {
        type: typeKey,
        name: taskType.name,
        count: groupData.count,
        percentage: totalTasks > 0 ? Math.round((groupData.count / totalTasks) * 100) : 0
      };
    })
    .sort((a, b) => {
      // Sort core types first, then others
      const aIndex = coreTypes.indexOf(a.type);
      const bIndex = coreTypes.indexOf(b.type);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.name.localeCompare(b.name);
    });

  const COLORS = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#06B6D4', '#EC4899', '#6366F1', '#14B8A6', '#D97706'
  ];

  // Status colors matching Timeline.tsx
  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      done: 'bg-green-100 text-green-800',
      completed: 'bg-green-100 text-green-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      pending: 'bg-gray-100 text-gray-800',
      blocked: 'bg-red-100 text-red-800',
      todo: 'bg-blue-100 text-blue-800',
      backlog: 'bg-purple-100 text-purple-800',
      requirements: 'bg-indigo-100 text-indigo-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  if (bootLoading || loading) {
    return (
      <Loader variant="overlay" size="xl" text="Loading summary..." />
    );
  }

  if (error || !summary) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium">{error || 'Failed to load summary'}</p>
          <button
            onClick={() => loadData()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6 pb-8">
        {/* Metric Cards - Top Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Completed Card */}
          <div
            onClick={() => handleStatusClick('completed')}
            className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md hover:border-green-300 transition-all cursor-pointer"
          >
            <div className="flex items-start gap-3">
              <div className="bg-green-100 p-2 rounded">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{completedLast7Days} completed</p>
                <p className="text-xs text-gray-600 mt-1">in the last 7 days</p>
              </div>
            </div>
          </div>

          {/* Updated Card */}
          <div
            onClick={handleUpdatedClick}
            className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer"
          >
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 p-2 rounded">
                <Edit className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{updatedLast7Days} updated</p>
                <p className="text-xs text-gray-600 mt-1">in the last 7 days</p>
              </div>
            </div>
          </div>

          {/* Created Card */}
          <div
            onClick={() => handleStatusClick('todo')}
            className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md hover:border-purple-300 transition-all cursor-pointer"
          >
            <div className="flex items-start gap-3">
              <div className="bg-purple-100 p-2 rounded">
                <CheckSquare className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{createdLast7Days} created</p>
                <p className="text-xs text-gray-600 mt-1">in the last 7 days</p>
              </div>
            </div>
          </div>

          {/* Due Soon Card */}
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md hover:border-orange-300 transition-all cursor-pointer" onClick={handleDueSoon}>
            <div className="flex items-start gap-3">
              <div className="bg-orange-100 p-2 rounded">
                <Calendar className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{dueSoon} due soon</p>
                <p className="text-xs text-gray-600 mt-1">in the next 7 days</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Grid - Status Overview & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Status Overview - Left Side (1 column) */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="mb-4">
              <h2 className="text-base font-semibold text-gray-900">Status overview</h2>
              <p className="text-sm text-gray-600 mt-1">
                Get a snapshot of the status of your work items.
                <button
                  onClick={handleViewAllClick}
                  className="text-blue-600 hover:text-blue-700 font-medium ml-1"
                >
                  View all work items
                </button>
              </p>
            </div>

            {/* Donut Chart */}
            <div className="flex items-center justify-center relative">
              {chartData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="35%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={1}
                        dataKey="value"
                        onClick={(entry) => handleStatusClick(entry.status)}
                        style={{ cursor: 'pointer' }}
                      >
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                            onClick={() => handleStatusClick(entry.status)}
                            style={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value}`} />
                    </PieChart>
                  </ResponsiveContainer>

                  {/* Center Text */}
                  <div className="absolute left-[35%] top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                    <p className="text-3xl font-bold text-gray-900">{summary.total_tasks}</p>
                    <p className="text-xs text-gray-600">Total work item...</p>
                  </div>

                  {/* Legend - Right Side */}
                  <div className="absolute right-6 top-1/2 transform -translate-y-1/2 space-y-2">
                    {chartData.map((item, index) => (
                      <button
                        key={item.status}
                        onClick={() => handleStatusClick(item.status)}
                        className="flex items-center gap-2 hover:opacity-80 text-left whitespace-nowrap"
                      >
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        ></div>
                        <span className="text-sm text-gray-700">{item.name}: {item.value}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  No tasks available
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity - Right Side (1 column) */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Recent activity</h2>
                <p className="text-sm text-gray-600 mt-1">Stay up to date with what's happening.</p>
              </div>
              <button className="text-gray-400 hover:text-gray-600">
                <ExternalLink className="w-5 h-5" />
              </button>
            </div>

            {/* Activity List */}
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {recentActivity.length > 0 ? (
                recentActivity.map((task) => (
                  <button
                    key={task.id}
                    onClick={() => handleTaskClick(task)}
                    className="block w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {task.title}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md ${getStatusColor(getStatusName(task))}`}>
                            {getStatusName(task) || 'No status'}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(task.updated_at || task.created_at || '').toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Priority Breakdown & Types of Work */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Priority Breakdown */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="mb-4">
              <h2 className="text-base font-semibold text-gray-900">Priority breakdown</h2>
              <p className="text-sm text-gray-600 mt-1">
                Get a holistic view of how work is being prioritized.
                <button
                  onClick={() => navigate(`/projects/${projectId}/list`)}
                  className="text-blue-600 hover:text-blue-700 font-medium ml-1"
                >
                  See what your team's been focusing on
                </button>
              </p>
            </div>

            {/* Bar Chart */}
            <div className="h-64">
              {priorityData.some(item => item.value > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={priorityData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 12 }}
                      interval={0}
                      onClick={(_, index) => {
                        const priority = priorityData[index]?.priority;
                        if (priority) {
                          handlePriorityClick(priority);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                              <p className="font-semibold">{label}</p>
                              <p className="text-sm text-gray-600">
                                {data.value} tasks
                              </p>
                              <button
                                onClick={() => handlePriorityClick(data.priority)}
                                className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                              >
                                View all {label.toLowerCase()} priority tasks
                              </button>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar
                      dataKey="value"
                      fill="#3B82F6"
                      onClick={(_, index) => {
                        const priority = priorityData[index]?.priority;
                        if (priority) {
                          handlePriorityClick(priority);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <p>No priority data available</p>
                </div>
              )}
            </div>
          </div>

          {/* Types of Work */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <div className="mb-4">
              <h2 className="text-base font-semibold text-gray-900">Types of work</h2>
              <p className="text-sm text-gray-600 mt-1">
                Get a breakdown of work items by their types.
                <button
                  onClick={() => navigate(`/projects/${projectId}/list`)}
                  className="text-blue-600 hover:text-blue-700 font-medium ml-1"
                >
                  View all items
                </button>
              </p>
            </div>

            {/* Type Table */}
            <div className="space-y-1">
              {/* Table Header */}
              <div className="grid grid-cols-2 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                <div>Type</div>
                <div>Distribution</div>
              </div>

              {/* Type Rows */}
              {typeData.length > 0 ? (
                typeData.map((item) => {
                  const matchingType = taskTypes.find(
                    (tt) => (tt.name || '').toLowerCase() === item.type
                  );
                  const iconRaw =
                    (matchingType as any)?.icon ||
                    (matchingType as any)?.icon_url ||
                    (matchingType as any)?.image ||
                    (matchingType as any)?.image_url;
                  const iconUrl = resolveTaskTypeIconUrl(iconRaw);

                  return (
                    <div
                      key={item.type}
                      className="group relative grid grid-cols-2 gap-4 py-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                      onClick={() => handleTypeClick(item.type)}
                    >
                      {/* Type Column */}
                      <div className="flex items-center gap-3">
                        <TaskTypeIcon url={iconUrl} name={item.name} className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900 capitalize">
                          {item.name || item.type}
                        </span>
                      </div>

                      {/* Distribution Column */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 relative">
                          <div
                            className="h-2 rounded-full bg-gray-400 transition-all duration-300"
                            style={{ width: `${item.percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600 w-12 text-right">
                          {item.percentage}%
                        </span>
                      </div>

                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        {item.percentage}% ({item.count}/{totalTasks} work items)
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No task types available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Task Modal (full details + actions) */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setSelectedTask(null);
          setActivity([]);
          setComments([]);
        }}
        task={selectedTask}
        // when updates happen, re-fetch full task from taskAPI to ensure modal shows updated fields
        onStatusUpdate={async (taskId, newStatus) => {
          await handleStatusUpdate(taskId, newStatus);
          try {
            const fresh = await taskAPI.getTask(taskId);
            setSelectedTask(fresh);
          } catch { /* ignore */ }
        }}
        onAddComment={async (taskId, html, parentId, plainText) => {
          const contentHtml = html;
          const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
          const createdComment = await commentAPI.createComment({
            task_id: taskId,
            content: contentText,
            content_html: contentHtml,
            content_text: contentText,
            parent_comment_id: parentId
          });
          await loadComments(taskId);
          return createdComment;
        }}
        onUpdateDescription={async (taskId, description) => {
          await dispatch(updateTask({ id: taskId, data: { description } }));
          try {
            const fresh = await taskAPI.getTask(taskId);
            setSelectedTask(fresh);
          } catch { /* ignore */ }
        }}
        onUpdateAssignee={async (taskId, assigneeId) => {
          await dispatch(updateTask({ id: taskId, data: { assigned_to: assigneeId === null ? null : assigneeId } }));
          try {
            const fresh = await taskAPI.getTask(taskId);
            setSelectedTask(fresh);
          } catch { /* ignore */ }
          // Refresh project members after assignee update
          if (selectedTask?.project_id || currentProject?.id) {
            await refreshProjectMembers(selectedTask?.project_id || currentProject?.id);
          }
        }}
        onUpdateReporter={async (taskId, reporterId) => {
          await dispatch(updateTask({ id: taskId, data: { reporter_id: reporterId === null ? null : reporterId } }));
          try {
            const fresh = await taskAPI.getTask(taskId);
            setSelectedTask(fresh);
          } catch { /* ignore */ }
          // Refresh project members after reporter update
          if (selectedTask?.project_id || currentProject?.id) {
            await refreshProjectMembers(selectedTask?.project_id || currentProject?.id);
          }
        }}
        activity={activity}
        taskDetails={selectedTask ? (() => {
          const rawLabels: any = (selectedTask as any)?.labels;
          const labels: string[] = Array.isArray(rawLabels)
            ? rawLabels
            : (typeof rawLabels === 'string'
              ? rawLabels.split(',').map((l: string) => l.trim()).filter(Boolean)
              : []);
          return {
            assignee: selectedTask.assigned_to || 'Unassigned',
            labels,
            sprint: selectedTask.sprint_id || 'Not set',
            dueDate: selectedTask.due_date || 'Not set'
          };
        })() : {}}
        projectMembers={projectMembers}
        comments={comments}
        onDeleteComment={async (commentId) => {
          await deleteComment(commentId);
          if (selectedTask) await loadComments(selectedTask.id);
        }}
        onUpdateComment={async (commentId, html, plainText) => {
          await updateComment(commentId, html, plainText);
          if (selectedTask) await loadComments(selectedTask.id);
        }}
        projectKey={currentProject?.key || 'AHM'}
        project={currentProject}
        onUpdateTaskTitle={async (id, title) => {
          await dispatch(updateTask({ id, data: { title } }));
          try {
            const fresh = await taskAPI.getTask(id);
            setSelectedTask(fresh);
          } catch { /* ignore */ }
        }}
        onUpdateFields={async (id, updates) => {
          // Don't call updateTask again - it's already been called by the component
          // Just refresh the task data
          try {
            const fresh = await taskAPI.getTask(id);
            setSelectedTask(fresh);
          } catch { /* ignore */ }
          // Refresh project members if assignee was updated
          if (updates.assigned_to !== undefined && (selectedTask?.project_id || currentProject?.id)) {
            await refreshProjectMembers(selectedTask?.project_id || currentProject?.id);
          }
        }}
        onOpenSubtask={(subtask: Subtask, parent: any) => {
          setParentTaskForSubtask(parent);
          setIsTaskModalOpen(false);
          setActiveSubtask(subtask);
          setIsSubtaskModalOpen(true);
        }}
      />

      {/* Subtask Modal */}
      {activeSubtask && (
        <SubtaskModal
          subtask={activeSubtask}
          isOpen={isSubtaskModalOpen}
          onClose={() => {
            setIsSubtaskModalOpen(false);
            setActiveSubtask(null);
            setParentTaskForSubtask(null);
          }}
          parentKey={`${currentProject?.key || 'AHM'}-${(parentTaskForSubtask as any)?.display_number ?? (selectedTask as any)?.display_number ?? ''}`.replace(/-$/, '')}
          parentTitle={(parentTaskForSubtask || selectedTask)?.title}
          parentTask={(parentTaskForSubtask || selectedTask) as any}
          onOpenParent={(_parent) => {
            setIsSubtaskModalOpen(false);
            setActiveSubtask(null);
            setParentTaskForSubtask(null);
            if (_parent) {
              setSelectedTask(_parent);
              setIsTaskModalOpen(true);
            }
          }}
          projectMembers={projectMembers}
          activity={activity}
          projectKey={currentProject?.key || 'AHM'}
          project={currentProject}
          onAddComment={async (taskId, html, _parentId, plainText) => {
            const contentHtml = html;
            const contentText = plainText ?? toPlainText(ensureHtml(html || ''));
            const created = await commentAPI.createComment({
              task_id: taskId,
              content: contentText,
              content_html: contentHtml,
              content_text: contentText
            });
            if (parentTaskForSubtask || selectedTask) await loadComments((parentTaskForSubtask || selectedTask)?.id || '');
            return created;
          }}
          onUpdateComment={async (commentId, html, plainText) => {
            await updateComment(commentId, html, plainText);
            if (parentTaskForSubtask || selectedTask) await loadComments((parentTaskForSubtask || selectedTask)?.id || '');
          }}
          onDeleteComment={async (commentId) => {
            await deleteComment(commentId);
            if (parentTaskForSubtask || selectedTask) await loadComments((parentTaskForSubtask || selectedTask)?.id || '');
          }}
          onSave={async (updates) => {
            await dispatch(updateSubtask({ subtaskId: activeSubtask.id, data: updates })).unwrap();
            // Refresh subtask data
            try {
              const fresh = await subtaskAPI.get(activeSubtask.id);
              setActiveSubtask(fresh);
            } catch (error) {
              console.error('Failed to refresh subtask:', error);
            }
            // Refresh project members after subtask update
            if (currentProject?.id) {
              await refreshProjectMembers(currentProject.id);
            }
          }}
          onDelete={async () => {
            setIsSubtaskModalOpen(false);
          }}
        />
      )}
    </>
  );
};